import React from 'react';
import LegalLayout, {LegalSection, LegalContactCard} from '../components/redesign/LegalLayout';
import site from '../data/site';
import '../assets/scss/redesign.scss';

const PrivacyPolicy = () => (
    <LegalLayout title="Privacy Policy">
        <p>
            Strategic Wellness Advocates LLC ("we," "us," or "our") respects your privacy and is committed to
            protecting the personal information you share with us. This Privacy Policy explains how we collect,
            use, disclose, and safeguard information when you visit our website or contact us through it. Please
            read it carefully. By using this website, you agree to the practices described here.
        </p>
        <p className="sw-legal__callout sw-legal__callout--info">
            This Privacy Policy covers our website only. Protected health information (PHI) that we create or
            receive while providing behavioral health services is governed by the Health Insurance Portability
            and Accountability Act (HIPAA) and our separate Notice of Privacy Practices, which is provided to
            clients at intake.
        </p>

        <LegalSection title="1. Information We Collect and How We Collect It">
            <p>
                <strong>Information you provide.</strong> When you complete our contact form, request an
                appointment, or otherwise reach out, we may collect your name, email address, phone number, the
                service you are interested in, and any information you include in your message.
            </p>
            <p>
                <strong>Information collected automatically.</strong> When you visit our website, we may
                automatically collect certain technical information such as your IP address, browser type,
                device type, pages visited, time spent on pages, and referring website. This information is
                collected through cookies and similar technologies.
            </p>
            <p>
                We ask that you do not submit detailed medical, treatment, or substance use history through the
                website contact form. Our intake team will collect that information through secure channels.
            </p>
        </LegalSection>

        <LegalSection title="2. How We Use Your Information">
            <p>We use the information we collect to:</p>
            <ul>
                <li>Respond to your inquiries and schedule appointments or consultations</li>
                <li>Verify insurance coverage and coordinate intake</li>
                <li>Communicate with you by phone, email, or text message where you have consented</li>
                <li>Operate, maintain, and improve our website</li>
                <li>Comply with legal, regulatory, and accreditation requirements</li>
                <li>Protect against fraud, abuse, and security threats</li>
            </ul>
        </LegalSection>

        <LegalSection title="3. SMS / Text Message Communications">
            <p>
                If you provide your phone number and check the consent box on our contact form, you agree to
                receive text messages from Strategic Wellness Advocates LLC related to your inquiry, appointment
                scheduling, reminders, and follow-up. Message and data rates may apply. Message frequency varies.
            </p>
            <p>
                You may opt out at any time by replying <strong>STOP</strong> to any message.
                Reply <strong>HELP</strong> for assistance, or call us at{' '}
                <a href={site.phones[0].href}>{site.phones[0].label}</a>.
            </p>
            <p>
                Mobile phone numbers and SMS consent will not be shared with or sold to third parties or
                affiliates for marketing or promotional purposes.
            </p>
        </LegalSection>

        <LegalSection title="4. How We Share Information">
            <p>We do not sell your personal information. We may share information only in the following circumstances:</p>
            <ul>
                <li>
                    <strong>Service providers</strong> who help us operate our website, host data, or deliver
                    communications, and who are bound to protect your information
                </li>
                <li>
                    <strong>Legal requirements</strong>, when disclosure is required by law, subpoena, court
                    order, or governmental request
                </li>
                <li>
                    <strong>Safety</strong>, when we believe disclosure is necessary to protect the rights,
                    property, or safety of any person
                </li>
                <li>
                    <strong>Business transfers</strong>, in connection with a merger, acquisition, or sale of
                    assets, subject to this Privacy Policy
                </li>
            </ul>
        </LegalSection>

        <LegalSection title="5. Cookies and Tracking">
            <p>
                Our website may use cookies and similar technologies to remember your preferences, understand how
                visitors use the site, and improve performance. You can set your browser to refuse cookies or
                alert you when cookies are being sent. Some features of the website may not function properly
                without cookies.
            </p>
        </LegalSection>

        <LegalSection title="6. Data Security">
            <p>
                We use reasonable administrative, technical, and physical safeguards to protect your information
                from unauthorized access, use, or disclosure. However, no method of transmission over the
                internet or electronic storage is completely secure, and we cannot guarantee absolute security.
            </p>
        </LegalSection>

        <LegalSection title="7. Data Retention">
            <p>
                We retain personal information submitted through the website for as long as needed to respond to
                your inquiry, provide services, and meet legal and regulatory obligations. Records that become
                part of a client file are retained in accordance with applicable Maryland and federal
                recordkeeping requirements.
            </p>
        </LegalSection>

        <LegalSection title="8. Your Choices and Rights">
            <p>
                You may request access to, correction of, or deletion of personal information you have submitted
                through the website by contacting us using the information below. You may opt out of text
                messages at any time by replying STOP, and you may unsubscribe from email communications by
                following the instructions in any message we send.
            </p>
        </LegalSection>

        <LegalSection title="9. Children's Privacy">
            <p>
                Our website is not directed to children under 13, and we do not knowingly collect personal
                information from children through the website. Inquiries regarding services for children and
                adolescents should be submitted by a parent or legal guardian. If you believe a child has
                provided us with personal information, please contact us so we can remove it.
            </p>
        </LegalSection>

        <LegalSection title="10. Third-Party Links">
            <p>
                Our website may contain links to third-party sites, including social media and mapping services.
                We are not responsible for the privacy practices or content of those sites and encourage you to
                review their privacy policies.
            </p>
        </LegalSection>

        <LegalSection title="11. Changes to This Policy">
            <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this page with a
                revised effective date. Your continued use of the website after changes are posted constitutes
                acceptance of the updated policy.
            </p>
        </LegalSection>

        <LegalSection title="12. Contact Us">
            <p>If you have questions about this Privacy Policy or our privacy practices, please contact us:</p>
            <LegalContactCard/>
        </LegalSection>
    </LegalLayout>
);

export default PrivacyPolicy;
